/**
 * Boot + orchestration (index.html:1830-2064, 2227-2229). Seul point qui câble UI, moteurs,
 * réseau et état. Les écrans sont des rendus purs ; les clics dynamiques sont délégués ici.
 */
import './ui/styles/theme.css';
import './ui/styles/screens.css';
import './ui/styles/modals.css';

import type { Diff, EngineStatus, PuzzleBase, Snapshot } from '@ordre-cache/shared';
import { NB_DEFIS, scoreFor, starBonus, systemRng } from '@ordre-cache/shared';

import { curN, G, GAMES, setSync, state } from './state';
import {
  clearAllData,
  frontierDefi,
  hasSeen,
  hasSeenDefi,
  isDefiUnlocked,
  loadProg,
  loadSeen,
  markSeenDefi,
  progOf,
  saveProg,
} from './storage';
import { addScore, loadScore, recordStars } from './score';
import { deep } from './engines/util';
import { getSettings, loadSettings, setSetting, type MotionMode } from './settings';
import { playSound, primeAudio } from './audio';
import { haptic } from './haptics';
import { initPwa } from './pwa';

import { hitCell } from './ui/board';
import { confetti } from './ui/fx';
import { updateMeta } from './ui/meta';
import { renderHome } from './ui/screens/home';
import { renderHub } from './ui/screens/hub';
import { renderPlay, type WinGain } from './ui/screens/play';
import { renderSettings } from './ui/screens/settings';
import {
  pickedAvatarValue,
  renderAvatarGrid,
  renderProfile,
  setPickedAvatar,
} from './ui/screens/profile';
import {
  AVATARS,
  createProfile,
  deleteProfile,
  loadProfiles,
  switchProfile,
} from './profile';
import { renderAccountCard } from './ui/screens/account';
import {
  initCloud,
  login as accountLogin,
  logout as accountLogout,
  register as accountRegister,
  sendPasswordReset,
  setOnAuthChange,
  setOnDataReplaced,
} from './account';
import { closeRules, maybeAutoRules, openRules } from './ui/rules-modal';

import { createRoom, genCode, type Room, type RoomCallbacks } from './net/room';
import type { TransportFactory } from './net/transport';
import { makeBcTransport } from './net/bc-transport';
import { makeWsTransport } from './net/ws-transport';
import { httpBase } from './net/config';

const $ = (id: string): HTMLElement => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} manquant`);
  return el;
};
const canvas = $('board') as HTMLCanvasElement;

let winFired: number | null = null;
let lastGain: WinGain | null = null;
let lastOk = false;
let room: Room | null = null;

/* ---------- cycle de vie du puzzle (index.html:1830-1856) ---------- */

/**
 * Un puzzle est « bon » s'il n'est PAS déjà résolu ET s'il a une solution atteignable
 * (solveIt sur une copie → gagné). Cette vérification tourne AUSSI en production : le joueur
 * ne voit jamais une partie sans solution, quelle que soit la taille de grille.
 */
function isGoodPuzzle(g: ReturnType<typeof G>, p: PuzzleBase): boolean {
  if (g.status(p).ok) return false; // déjà résolu
  const probe = deep(p);
  g.resetSel?.();
  g.solveIt(probe);
  const st = g.status(probe);
  g.resetSel?.();
  return st.ok || !!st.prog;
}

function genPuzzle(): void {
  const g = G();
  g.resetSel?.();
  let p = g.gen(state.defi, curN(), state.diff, systemRng);
  for (let t = 0; t < 20 && !isGoodPuzzle(g, p); t++) {
    g.resetSel?.();
    p = g.gen(state.defi, curN(), state.diff, systemRng);
  }
  if (import.meta.env.DEV && !isGoodPuzzle(g, p))
    console.error('[Tuilya] aucun puzzle solvable après 20 tentatives', deep(p));
  g.resetSel?.();
  state.puz = p;
  state.puz.mv = 0;
  state.puz.t0 = Date.now();
  winFired = null;
}

function checkWin(st: EngineStatus): void {
  const p = state.puz;
  if (!p) return;
  if (st.ok && winFired !== p.pid) {
    winFired = p.pid;
    if (p._ft == null) p._ft = Math.max(0, Date.now() - (p.t0 ?? Date.now()));
    const prog = progOf(G().id, state.diff);
    const first = !prog[state.defi];
    if (first) {
      prog[state.defi] = true;
      saveProg();
    }
    // Barème performance : coups vs par + temps → points + étoiles 1-3.
    const par = p.par ?? Math.max(4, Math.round((p.N * p.N) / 4));
    const res = scoreFor(state.diff, state.sizeIdx, par, p.mv ?? 0, p._ft ?? 0);
    const prevStars = recordStars(G().id, state.diff, state.defi, res.stars);
    // Rejeu : 25 % de la base, mais une amélioration d'étoiles paie sa différence plein tarif.
    const pts = first
      ? res.pts
      : Math.round((res.pts - res.bonus) * 0.25) +
        Math.max(0, res.bonus - starBonus(prevStars, state.diff));
    addScore(G().id, pts);
    lastGain = { pid: p.pid, pts, stars: res.stars, first };
    playSound('win');
    haptic('win');
    confetti([G().accent, '#ffd166', '#ffffff', '#ff9e6d']); // visuel seul (coupé en motion réduit)
  }
}

/* ---------- rendu (index.html:1858-1931) ---------- */

function showScreen(s: 'home' | 'hub' | 'play'): void {
  state.screen = s;
  document.body.dataset.screen = s; // pilote la visibilité du bouton ⚙️ (accueil seul)
  ['scrHome', 'scrHub', 'scrPlay'].forEach((id) => $(id).classList.remove('on'));
  $(s === 'home' ? 'scrHome' : s === 'hub' ? 'scrHub' : 'scrPlay').classList.add('on');
  render();
}

/** Vies restantes d'un puzzle, si le jeu en a (Vitrail/Duo/Ponts), sinon `null`. */
function livesOf(p: PuzzleBase | null): number | null {
  const lv = (p as unknown as { lives?: number | null } | null)?.lives;
  return typeof lv === 'number' ? lv : null;
}

function renderPlayScreen(): void {
  const g = G();
  const p = state.puz;
  if (!p || p.g !== g.id) genPuzzle();
  const st = renderPlay({ canvas, checkWin, getLastGain: () => lastGain });
  lastOk = st.ok;
}

function render(): void {
  if (state.screen === 'home') renderHome();
  else if (state.screen === 'hub') renderHub();
  else renderPlayScreen();
}

function netPush(): void {
  room?.push();
}

function sync(): void {
  render();
  netPush();
}
setSync(sync);

/* ---------- réseau (index.html:2077-2154) ---------- */

function buildSnapshot(): Snapshot {
  return {
    screen: state.screen,
    diff: state.diff,
    sizeIdx: state.sizeIdx,
    game: state.game,
    defi: state.defi,
    puz: state.puz ? (deep(state.puz) as unknown as Record<string, unknown>) : null,
  };
}

function applyRemote(d: Snapshot): void {
  const incoming = d.puz as PuzzleBase | null;
  const puzChanged = !state.puz || !incoming || incoming.pid !== state.puz.pid;
  state.diff = d.diff as Diff;
  state.sizeIdx = d.sizeIdx;
  state.game = d.game;
  state.defi = d.defi;
  if (incoming) state.puz = deep(incoming);
  if (puzChanged) GAMES.forEach((g) => g.resetSel?.());
  showScreen(d.screen);
}

function hideRoomTag(): void {
  const tag = document.getElementById('roomTag');
  if (tag) tag.style.display = 'none';
  document.body.classList.remove('inRoom');
}

const roomCb: RoomCallbacks = {
  buildSnapshot,
  applyRemote,
  onPresence(count) {
    const rp = document.getElementById('roomPlayers');
    if (rp) rp.textContent = `👥 ${count} joueur${count > 1 ? 's' : ''}`;
    const tag = document.getElementById('roomTag');
    if (room && tag) {
      tag.style.display = 'block';
      tag.innerHTML = `🌐 <b>${room.code()}</b> · 👥 ${count}`;
      document.body.classList.add('inRoom');
    }
  },
  onOpen(code) {
    $('netStatus').textContent = '';
    document.getElementById('netHome')?.classList.add('hidden');
    document.getElementById('netRoom')?.classList.remove('hidden');
    $('roomCode').textContent = code;
    document.getElementById('btnNet')?.classList.add('on');
    playSound('room'); // entrée/création de salle
    haptic('room');
  },
  onError(type) {
    let msg = 'Erreur réseau.';
    if (type === 'room_not_found') msg = 'Salle introuvable — vérifie le code.';
    else if (type === 'room_full') msg = 'Salle pleine (8 joueurs maximum).';
    else if (type === 'rate_limited') msg = 'Trop de tentatives — réessaie dans un instant.';
    else if (type === 'network') msg = 'Réseau indisponible (hors ligne ?).';
    $('netStatus').textContent = msg;
    room?.leave();
    room = null;
    document.getElementById('btnNet')?.classList.remove('on');
    hideRoomTag();
  },
  onConnectionLost() {
    room = null;
    hideRoomTag();
    document.getElementById('btnNet')?.classList.remove('on');
    $('netStatus').textContent = 'Connexion perdue.';
    document.getElementById('netModal')?.classList.remove('hidden');
  },
};

function startNet(creator: boolean, code: string): void {
  if (room) {
    room.leave();
    room = null;
  }
  const make: TransportFactory = isLoop()
    ? (c, id, onEvent) => makeBcTransport(c, id, onEvent)
    : (c, id, onEvent) => makeWsTransport(c, id, onEvent);
  room = createRoom({ creator, code, make, cb: roomCb });
}

/** Transport de test/dev hors-ligne (BroadcastChannel) si `?loop=1`, sinon WebSocket (serveur). */
function isLoop(): boolean {
  return location.search.indexOf('loop=1') >= 0;
}

/**
 * Création d'une salle : en WebSocket, le code est généré côté serveur (`POST /rooms`,
 * docs/03 §2.5). En mode `loop` (BroadcastChannel, sans serveur), on le tire localement.
 */
async function createRoomFlow(): Promise<void> {
  $('netStatus').textContent = 'Création de la salle…';
  if (isLoop()) {
    startNet(true, genCode());
    return;
  }
  const base = httpBase();
  if (!base) {
    $('netStatus').textContent = 'Multijoueur indisponible (serveur non configuré).';
    return;
  }
  try {
    const res = await fetch(`${base}/rooms`, { method: 'POST' });
    if (!res.ok) throw new Error(String(res.status));
    const { code } = (await res.json()) as { code: string };
    startNet(true, code);
  } catch {
    $('netStatus').textContent = 'Réseau indisponible (hors ligne ?).';
  }
}

function leaveRoom(): void {
  room?.leave();
  room = null;
  document.getElementById('btnNet')?.classList.remove('on');
  hideRoomTag();
  document.getElementById('netRoom')?.classList.add('hidden');
  document.getElementById('netHome')?.classList.remove('hidden');
}

/* ---------- interactions (index.html:2026-2065) ---------- */

canvas.addEventListener('pointerdown', (e) => {
  if (!state.puz) return;
  const rect = canvas.getBoundingClientRect();
  const g = G();
  const hit = hitCell(state.puz, e.clientX - rect.left, e.clientY - rect.top, rect.width);
  if (!hit) return;
  const before = livesOf(state.puz);
  const res = g.tap(state.puz, hit.r, hit.c);
  if (res === false) {
    playSound('refuse'); // geste refusé : toc mat
    haptic('refuse');
    return;
  }
  if (res === true && state.puz) state.puz.mv = (state.puz.mv ?? 0) + 1; // compteur partagé
  const after = livesOf(state.puz);
  if (before != null && after != null && after < before) {
    playSound('lifeLost'); // vie perdue : note descendante (prioritaire sur le tic)
    haptic('lifeLost');
  } else {
    playSound('tap'); // tic doux
  }
  renderPlayScreen();
  if (res !== 'ui') netPush(); // "ui" = sélection locale, rien à diffuser
});

$('btnNew').addEventListener('click', () => {
  const g = G();
  const ex = state.puz ? (g.extra?.(state.puz) ?? null) : null;
  if (ex && g.advance && state.puz) {
    state.puz = g.advance(state.puz, state.diff, systemRng);
    state.puz.mv = 0;
    state.puz.t0 = Date.now();
    winFired = null;
    playSound('round'); // manche Marathon suivante
    haptic('round');
  } else {
    genPuzzle();
  }
  renderPlayScreen();
  netPush();
});

/* ---------- intro de défi (première découverte d'un (jeu, défi)) ---------- */

function openDefiIntro(): void {
  const g = G();
  const d = g.defis[state.defi];
  const set = (id: string, txt: string): void => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };
  set('diIcon', d.ic ?? '🎯');
  set('diKicker', `${g.icon} ${g.name} — Défi ${state.defi + 1}/${NB_DEFIS}`);
  set('diName', d.n);
  set('diDesc', d.d);
  document.getElementById('defiModal')?.classList.remove('hidden');
}

/** Montre l'intro si ce défi n'a jamais été vu (le défi 1 est couvert par les règles auto). */
function maybeDefiIntro(): void {
  if (state.defi === 0) return;
  if (hasSeenDefi(G().id, state.defi)) return;
  markSeenDefi(G().id, state.defi);
  openDefiIntro();
}

$('diGo').addEventListener('click', () => $('defiModal').classList.add('hidden'));
$('defiModal').addEventListener('click', (e) => {
  if (e.target === $('defiModal')) $('defiModal').classList.add('hidden');
});

function gotoDefi(i: number): void {
  const t = ((i % NB_DEFIS) + NB_DEFIS) % NB_DEFIS;
  // Parcours à déblocage : un défi verrouillé ne s'ouvre pas (geste refusé).
  if (!isDefiUnlocked(G().id, state.diff, t)) {
    playSound('refuse');
    haptic('refuse');
    return;
  }
  state.defi = t;
  genPuzzle();
  sync();
  maybeDefiIntro();
  // Attire le regard sur la nouvelle mission (pulse accent, coupé en motion réduit).
  const mc = document.getElementById('missionCard');
  if (mc) {
    mc.classList.remove('flash');
    void mc.offsetWidth;
    mc.classList.add('flash');
  }
}

/** ◀ ▶ : saute au défi débloqué le plus proche dans la direction demandée. */
function stepDefi(dir: number): void {
  let i = state.defi;
  for (let k = 0; k < NB_DEFIS; k++) {
    i = (i + dir + NB_DEFIS) % NB_DEFIS;
    if (isDefiUnlocked(G().id, state.diff, i)) {
      gotoDefi(i);
      return;
    }
  }
}

$('prevDefi').addEventListener('click', () => stepDefi(-1));
$('nextDefi').addEventListener('click', () => stepDefi(1));

/* ---------- carte mission (repli/dépli) + carte de victoire ---------- */

$('missionCard').addEventListener('click', () => {
  const mc = $('missionCard');
  const open = mc.getAttribute('aria-expanded') === 'true';
  mc.setAttribute('aria-expanded', String(!open));
  $('mMore').classList.toggle('hidden', open);
});

$('winAgain').addEventListener('click', () => {
  genPuzzle();
  renderPlayScreen();
  netPush();
});
$('winNext').addEventListener('click', () => {
  // Dernier défi du jeu terminé → retour au choix des casse-têtes.
  if (state.defi >= NB_DEFIS - 1) {
    showScreen('hub');
    sync();
    return;
  }
  gotoDefi(state.defi + 1);
});
$('btnRules').addEventListener('click', openRules);
$('playBack').addEventListener('click', () => {
  showScreen('hub');
  sync();
});
$('hubBack').addEventListener('click', () => {
  showScreen('home');
  sync();
});
$('btnStart').addEventListener('click', () => {
  showScreen('hub');
  sync();
});
$('btnHomeNet').addEventListener('click', openNetModal);

// Le niveau et la taille se règlent désormais sur l'écran de jeu (contrôles `playDiff`/`playSize`),
// plus sur l'accueil : moins d'étapes avant de jouer.

// Clics dynamiques délégués sur des conteneurs statiques.
$('gameGrid').addEventListener('click', (e) => {
  const card = (e.target as HTMLElement).closest<HTMLElement>('.gCard');
  if (!card || card.dataset.i == null) return;
  const i = Number(card.dataset.i);
  const g = GAMES[i];
  const resume =
    state.puz && state.puz.g === g.id && isDefiUnlocked(g.id, state.diff, state.defi);
  state.game = i;
  if (!resume) {
    state.defi = frontierDefi(g.id, state.diff); // reprise au front du parcours
    genPuzzle();
  }
  showScreen('play');
  sync();
  maybeAutoRules();
  // Reprise plus loin que le défi 1 : présenter le défi si c'est sa découverte
  // (jamais par-dessus les règles, qui ne s'ouvrent qu'à la toute 1re visite).
  if (!resume && hasSeen(g.id)) maybeDefiIntro();
});
$('defiDots').addEventListener('click', (e) => {
  const tab = (e.target as HTMLElement).closest<HTMLElement>('.defiTab');
  if (!tab || tab.dataset.i == null) return;
  if (tab.classList.contains('locked')) {
    // Verrouillé : petit refus (son + secousse), le mystère reste entier.
    playSound('refuse');
    haptic('refuse');
    tab.classList.remove('deny');
    void tab.offsetWidth;
    tab.classList.add('deny');
    return;
  }
  gotoDefi(Number(tab.dataset.i));
});
$('playSize').addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('button');
  if (!b || b.dataset.s == null) return;
  state.sizeIdx = Number(b.dataset.s);
  genPuzzle();
  renderPlayScreen();
  netPush();
});
$('playDiff').addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('button');
  if (!b || b.dataset.d == null) return;
  state.diff = Number(b.dataset.d) as Diff;
  // La progression est par niveau : si le défi courant n'y est pas débloqué,
  // on reprend au front du parcours de ce niveau.
  if (!isDefiUnlocked(G().id, state.diff, state.defi))
    state.defi = frontierDefi(G().id, state.diff);
  genPuzzle();
  renderPlayScreen();
  netPush();
});
window.addEventListener('resize', () => {
  if (state.screen === 'play') renderPlayScreen();
});

/* ---------- modales règles + réseau (index.html:2021-2023, 2185-2193) ---------- */

$('rulesClose').addEventListener('click', closeRules);
$('rulesGo').addEventListener('click', closeRules);
$('rulesModal').addEventListener('click', (e) => {
  if (e.target === $('rulesModal')) closeRules();
});

/* Multijoueur hors ligne : état « connexion requise » propre (tâche 2.3). Le solo, lui,
   fonctionne 100 % hors ligne via le service worker (pwa.ts). */
const OFFLINE_MSG = "📡 Connexion requise — le multijoueur a besoin d'Internet.";

function refreshNetAvailability(): void {
  const offline = !navigator.onLine;
  for (const id of ['netCreate', 'netJoin']) {
    (document.getElementById(id) as HTMLButtonElement | null)?.toggleAttribute('disabled', offline);
  }
  (document.getElementById('netCode') as HTMLInputElement | null)?.toggleAttribute(
    'disabled',
    offline,
  );
  const st = $('netStatus');
  if (offline) st.textContent = OFFLINE_MSG;
  else if (st.textContent === OFFLINE_MSG) st.textContent = '';
}

function openNetModal(): void {
  refreshNetAvailability();
  document.getElementById('netModal')?.classList.remove('hidden');
}

window.addEventListener('online', refreshNetAvailability);
window.addEventListener('offline', refreshNetAvailability);

$('btnNet').addEventListener('click', openNetModal);
$('netClose').addEventListener('click', () =>
  document.getElementById('netModal')?.classList.add('hidden'),
);
$('netModal').addEventListener('click', (e) => {
  if (e.target === $('netModal')) document.getElementById('netModal')?.classList.add('hidden');
});
$('netCreate').addEventListener('click', () => {
  void createRoomFlow();
});
$('netJoin').addEventListener('click', () => {
  const c = ((document.getElementById('netCode') as HTMLInputElement).value || '')
    .trim()
    .toUpperCase();
  if (c.length < 3) {
    $('netStatus').textContent = 'Entre le code de la salle.';
    return;
  }
  $('netStatus').textContent = 'Connexion…';
  startNet(false, c);
});
$('netLeave').addEventListener('click', leaveRoom);
$('netCode').addEventListener('keydown', (e) => {
  if ((e as KeyboardEvent).key === 'Enter') $('netJoin').click();
});

/* ---------- Réglages (F-SETTINGS) — écran overlay 100 % local, jamais synchronisé ---------- */

function openSettings(): void {
  renderSettings();
  $('scrSettings').classList.add('on');
  document.body.dataset.overlay = 'settings';
}
function closeSettings(): void {
  clearPending();
  $('scrSettings').classList.remove('on');
  document.body.dataset.overlay = '';
}

$('btnSettings').addEventListener('click', openSettings);
$('setBack').addEventListener('click', closeSettings);

/* ---------- À propos / confidentialité (tâche 2.5) — overlay 100 % local ---------- */

$('btnAbout').addEventListener('click', () => {
  $('scrAbout').classList.add('on');
  document.body.dataset.overlay = 'about';
});
$('aboutBack').addEventListener('click', () => {
  $('scrAbout').classList.remove('on');
  document.body.dataset.overlay = '';
});

/* ---------- Profils locaux (multi-joueurs sur un appareil) — overlay 100 % local ---------- */

/** Recharge en mémoire la progression/score/« déjà vu » du profil actif. */
function reloadProfileData(): void {
  loadProg();
  loadScore();
  loadSeen();
}

function closeCreate(): void {
  clearDelPending();
  $('profCreate').classList.add('hidden');
}
function openProfile(): void {
  closeCreate();
  renderAccountCard();
  renderProfile();
  $('scrProfile').classList.add('on');
  document.body.dataset.overlay = 'profile';
}
function closeProfile(): void {
  closeCreate();
  $('scrProfile').classList.remove('on');
  document.body.dataset.overlay = '';
  render(); // l'accueil reflète le profil éventuellement changé
}

$('btnProfile').addEventListener('click', openProfile);
$('profBack').addEventListener('click', closeProfile);

/** Bascule sur un profil (id vide = invité), recharge ses données, revient à l'accueil. */
function useProfile(id: string | null): void {
  switchProfile(id);
  reloadProfileData();
  playSound('room');
  haptic('room');
  closeProfile();
}

// Suppression en deux temps sur le bouton 🗑 (pas de dialog natif).
let delTimer: number | null = null;
let delArmed: string | null = null;
function clearDelPending(): void {
  if (delTimer != null) {
    clearTimeout(delTimer);
    delTimer = null;
  }
  delArmed = null;
  document
    .querySelectorAll<HTMLElement>('#profList .profDel.armed')
    .forEach((b) => b.classList.remove('armed'));
}

$('profList').addEventListener('click', (e) => {
  const target = e.target as HTMLElement;
  // 1) bouton supprimer (deux temps)
  const del = target.closest<HTMLElement>('[data-del]');
  if (del) {
    const id = del.dataset.del!;
    if (delArmed === id) {
      clearDelPending();
      deleteProfile(id); // efface le profil ET ses données ; repasse en invité si actif
      reloadProfileData();
      renderProfile();
    } else {
      clearDelPending();
      delArmed = id;
      del.classList.add('armed');
      del.textContent = '✓';
      delTimer = window.setTimeout(() => {
        del.textContent = '🗑';
        clearDelPending();
      }, 3000);
    }
    return;
  }
  clearDelPending();
  // 2) carte « nouveau profil »
  if (target.closest('#profNewCard')) {
    setPickedAvatar(AVATARS[0]);
    ($('profName') as HTMLInputElement).value = '';
    renderAvatarGrid();
    $('profCreate').classList.remove('hidden');
    $('profCreate').scrollIntoView({ block: 'nearest' });
    ($('profName') as HTMLInputElement).focus();
    return;
  }
  // 3) carte de profil (ou invité) → bascule
  const card = target.closest<HTMLElement>('.profCard[data-pid]');
  if (card) useProfile(card.dataset.pid || null);
});

$('avatarGrid').addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('[data-av]');
  if (b?.dataset.av) setPickedAvatar(b.dataset.av);
});

$('profSave').addEventListener('click', () => {
  const name = ($('profName') as HTMLInputElement).value;
  createProfile(name, pickedAvatarValue()); // crée ET rend actif
  reloadProfileData();
  playSound('room');
  haptic('room');
  closeProfile();
});
$('profCancel').addEventListener('click', closeCreate);
$('profName').addEventListener('keydown', (e) => {
  if ((e as KeyboardEvent).key === 'Enter') $('profSave').click();
});

/* ---------- Compte cloud (connexion facultative, sauvegarde en ligne) ---------- */

/** Recharge tout l'état local (registre + données du profil actif) et re-rend. */
function reloadAll(): void {
  loadProfiles();
  reloadProfileData();
  renderAccountCard();
  render();
}
// Quand une sauvegarde cloud remplace les données locales (connexion / pull), on recharge.
setOnDataReplaced(reloadAll);
// Quand l'état de connexion change (login/logout/reprise de session), on rafraîchit l'UI.
setOnAuthChange(() => {
  renderAccountCard();
  render();
});

type AuthMode = 'login' | 'register' | 'reset';
let authMode: AuthMode = 'login';

function setAuthMode(m: AuthMode): void {
  authMode = m;
  const set = (id: string, txt: string): void => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };
  const show = (id: string, on: boolean): void => {
    document.getElementById(id)?.classList.toggle('hidden', !on);
  };
  $('authStatus').textContent = '';
  const pass = $('authPass') as HTMLInputElement;
  show('authPass', m !== 'reset'); // en réinitialisation : email seul
  show('authToRegister', m === 'login');
  show('authToLogin', m !== 'login');
  show('authToReset', m === 'login');
  if (m === 'login') {
    set('authTitle', 'Se connecter');
    set('authInfo', 'Retrouve ta progression sur tous tes appareils.');
    set('authSubmit', 'Se connecter');
    pass.placeholder = 'Mot de passe';
    pass.autocomplete = 'current-password';
  } else if (m === 'register') {
    set('authTitle', 'Créer un compte');
    set('authInfo', 'Un email et un mot de passe suffisent. Ta progression actuelle sera sauvegardée.');
    set('authSubmit', 'Créer mon compte');
    pass.placeholder = 'Mot de passe';
    pass.autocomplete = 'new-password';
  } else {
    set('authTitle', 'Mot de passe oublié');
    set('authInfo', 'Entre ton email : tu recevras un lien pour choisir un nouveau mot de passe.');
    set('authSubmit', 'Envoyer le lien');
  }
}

function openAuth(): void {
  $('authForm').classList.remove('hidden');
  $('authDone').classList.add('hidden');
  (['authEmail', 'authPass'] as const).forEach((id) => {
    ($(id) as HTMLInputElement).value = '';
  });
  setAuthMode('login');
  document.getElementById('authModal')?.classList.remove('hidden');
}
function closeAuth(): void {
  document.getElementById('authModal')?.classList.add('hidden');
}

async function submitAuth(): Promise<void> {
  const email = ($('authEmail') as HTMLInputElement).value.trim();
  const pass = ($('authPass') as HTMLInputElement).value;
  const status = $('authStatus');
  const btn = $('authSubmit') as HTMLButtonElement;
  status.textContent = '…';
  btn.disabled = true;
  try {
    if (authMode === 'reset') {
      const res = await sendPasswordReset(email);
      if (!res.ok) {
        status.textContent = res.error;
        return;
      }
      $('authDoneMsg').textContent = `Si un compte existe pour ${email}, un email avec un lien de réinitialisation vient d'être envoyé. Pense à vérifier tes spams.`;
      $('authForm').classList.add('hidden');
      $('authDone').classList.remove('hidden');
      return;
    }
    const res =
      authMode === 'login' ? await accountLogin(email, pass) : await accountRegister(email, pass);
    if (!res.ok) {
      status.textContent = res.error;
      return;
    }
    // login/register OK : la connexion (onAuth) + le pull rafraîchissent l'UI.
    closeAuth();
    reloadAll();
    playSound('room');
    haptic('room');
  } finally {
    btn.disabled = false;
  }
}

$('accountCard').addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  if (t.closest('#acLogin')) openAuth();
  else if (t.closest('#acLogout')) void accountLogout(); // onAuthChange re-rend
});
$('authClose').addEventListener('click', closeAuth);
$('authModal').addEventListener('click', (e) => {
  if (e.target === $('authModal')) closeAuth();
});
$('authSubmit').addEventListener('click', () => void submitAuth());
$('authPass').addEventListener('keydown', (e) => {
  if ((e as KeyboardEvent).key === 'Enter') void submitAuth();
});
$('authEmail').addEventListener('keydown', (e) => {
  if ((e as KeyboardEvent).key === 'Enter' && authMode === 'reset') void submitAuth();
});
$('authToRegister').addEventListener('click', () => setAuthMode('register'));
$('authToLogin').addEventListener('click', () => setAuthMode('login'));
$('authToReset').addEventListener('click', () => setAuthMode('reset'));
$('authDoneBtn').addEventListener('click', closeAuth);

$('setSound').addEventListener('click', () => {
  const on = !getSettings().sound;
  setSetting('sound', on);
  renderSettings();
  if (on) {
    primeAudio();
    playSound('tap'); // aperçu audible + déblocage audio dans le geste
  }
});

$('setHaptics').addEventListener('click', () => {
  const on = !getSettings().haptics;
  setSetting('haptics', on);
  renderSettings();
  if (on) haptic('room'); // aperçu tactile
});

$('setVolume').addEventListener('input', (e) => {
  setSetting('volume', Number((e.target as HTMLInputElement).value) / 100);
});
$('setVolume').addEventListener('change', () => {
  primeAudio();
  playSound('tap'); // aperçu au relâchement
});

$('setMotion').addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>('button');
  if (!b || !b.dataset.m) return;
  setSetting('motion', b.dataset.m as MotionMode);
  renderSettings();
});

// Effacement destructif en deux temps (pas de dialog natif) : 1er clic arme, 2e clic efface.
let clearTimer: number | null = null;
function clearPending(): void {
  if (clearTimer != null) {
    clearTimeout(clearTimer);
    clearTimer = null;
  }
  const b = document.getElementById('setClear');
  if (b) {
    b.textContent = 'Effacer';
    b.classList.remove('armed');
  }
}
$('setClear').addEventListener('click', () => {
  const b = $('setClear');
  if (clearTimer == null) {
    b.textContent = 'Confirmer ?';
    b.classList.add('armed');
    clearTimer = window.setTimeout(clearPending, 4000);
    return;
  }
  clearAllData();
  location.reload();
});

setInterval(() => {
  if (state.screen === 'play' && state.puz) updateMeta(lastOk);
}, 1000);

/* ---------- hook dev/test (index.html:2196-2225) — exposé en build dev uniquement ---------- */

if (import.meta.env.DEV || /[?&](dev|loop)=1/.test(location.search)) {
  interface DevHook {
    state(): { screen: string; diff: number; sizeIdx: number; game: number; defi: number };
    puz(): PuzzleBase | null;
    go(g: number, defi: number, sizeIdx: number, diff: number): void;
    status(): EngineStatus;
    solveIt(): void;
    tap(r: number, c: number): void;
    peers(): number;
    code(): string;
    adopted(): boolean;
    connected(): boolean;
  }
  const hook: DevHook = {
    state: () => ({
      screen: state.screen,
      diff: state.diff,
      sizeIdx: state.sizeIdx,
      game: state.game,
      defi: state.defi,
    }),
    puz: () => (state.puz ? deep(state.puz) : null),
    go: (g, defi, sizeIdx, diff) => {
      state.game = g;
      state.defi = defi;
      state.sizeIdx = sizeIdx;
      state.diff = diff as Diff;
      genPuzzle();
      showScreen('play');
    },
    status: () => G().status(state.puz as PuzzleBase),
    solveIt: () => {
      if (!state.puz) return;
      G().solveIt(state.puz);
      renderPlayScreen();
      netPush();
    },
    tap: (r, c) => {
      if (!state.puz) return;
      const res = G().tap(state.puz, r, c);
      if (res) {
        if (res === true && state.puz) state.puz.mv = (state.puz.mv ?? 0) + 1;
        renderPlayScreen();
        if (res !== 'ui') netPush();
      }
    },
    peers: () => room?.peerCount() ?? 1,
    code: () => room?.code() ?? '',
    adopted: () => room?.adopted() ?? false,
    connected: () => !!room,
  };
  (window as unknown as { __t: DevHook }).__t = hook;
}

/* ---------- boot (index.html:2228-2229) ---------- */

loadSettings();
loadProfiles(); // AVANT les données : elles se chargent depuis les clés du profil actif
loadProg();
loadScore();
loadSeen();
initPwa();
// Compte cloud : branche l'observateur de session (reprise + pull multi-appareils si connecté).
initCloud();
$('aboutVersion').textContent = `Version ${__APP_VERSION__} — PWA installable, solo hors ligne`;
// Débloque l'audio au tout premier geste (politique autoplay), même hors plateau.
window.addEventListener('pointerdown', () => primeAudio(), { once: true });
showScreen('home');
